import { useEffect, useState } from "react";
import { InitialsAvatar } from "../../components/InitialsAvatar";
import { api, BASE } from "../../lib/api";
import { useResource, formatDate, titleCase } from "../../lib/hooks";
import { FilterBar, matches } from "../../components/FilterBar";
import { EntityForm, Field } from "../../components/EntityForm";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { PrintDoc } from "../../components/PrintDoc";
import type { Company, Employee, JobRole } from "../../lib/types";

const money = (value: string | number): string =>
    Number(value).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

type Tab = "summary" | "expenses" | "rates" | "statements";

interface MarginSummary { billed: number; cost: number; margin: number; }

interface Summary {
    fy: number;
    income: {
        income_paid: string; income_outstanding: string; gst_collected: string;
        discounts_given: string; invoices_paid: string; invoices_outstanding: string;
    };
    wages: { wages_paid: string; super_paid: string; payg_withheld: string; wages_approved_unpaid: string };
    expenses: { other_expenses: string; gst_paid: string };
    totalExpenditure: number;
    profit: number;
    margin: MarginSummary;
}

interface ExpenseRow {
    id: string; expense_date: string; category: string; description: string;
    supplier: string | null; amount: string; gst_amount: string;
}

interface RateRow { scope: "pay" | "bill" | "pay_casual"; category: string; multiplier: string | number; }

const CATEGORIES = [
    ["weekday_day", "Weekday day"],
    ["weekday_evening", "Weekday evening (6pm–10pm)"],
    ["weekday_night", "Weekday night (10pm–6am)"],
    ["saturday", "Saturday"],
    ["sunday", "Sunday"],
    ["public_holiday", "Public holiday"],
] as const;

const downloadPayrollCsv = async (fy: number): Promise<void> => {
    const token = localStorage.getItem("trustcare.token");
    const response = await fetch(`${BASE}/finance/payroll-export?fy=${fy}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `payroll-fy${fy}.csv`;
    link.click();
    URL.revokeObjectURL(url);
};

const currentFy = (): number => {
    const now = new Date();
    return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
};

/**
 * The money page: FY summary for the tax-return picture, expense capture,
 * rate administration, and on-demand year-end statements. Every figure is
 * derived live from invoices, timesheets and expenses — no second ledger.
 */
export const Finance = () => {
    const [tab, setTab] = useState<Tab>("summary");
    const [fy, setFy] = useState(currentFy());

    return (
        <div>
            <div className="tabs">
                {(["summary", "expenses", "rates", "statements"] as Tab[]).map((value) => (
                    <button key={value} className={tab === value ? "active" : ""} onClick={() => setTab(value)}>
                        {titleCase(value)}
                    </button>
                ))}
                <div style={{ marginLeft: "auto" }}>
                    <select className="filter-select" value={fy} onChange={(e) => setFy(Number(e.target.value))}
                        aria-label="Financial year">
                        {[currentFy() + 1, currentFy(), currentFy() - 1, currentFy() - 2].map((year) => (
                            <option key={year} value={year}>FY {year - 1}–{String(year).slice(2)}</option>
                        ))}
                    </select>
                </div>
            </div>

            {tab === "summary" && <SummaryTab fy={fy} />}
            {tab === "expenses" && <ExpensesTab />}
            {tab === "rates" && <RatesTab />}
            {tab === "statements" && <StatementsTab fy={fy} />}
        </div>
    );
};

const SummaryTab = ({ fy }: { fy: number }) => {
    const summary = useResource<Summary>(`/finance/summary?fy=${fy}`);
    const data = summary.data;
    if (summary.error) return <div className="error-banner">{summary.error}</div>;
    if (!data) return <p style={{ color: "var(--muted)" }}>Loading…</p>;

    return (
        <div style={{ display: "grid", gap: "1rem" }}>
            <div className="grid cols-4">
                <div className="card stat">
                    <div className="label">Income received</div>
                    <div className="value" style={{ color: "var(--pass)" }}>{money(data.income.income_paid)}</div>
                    <div className="sub">{data.income.invoices_paid} paid invoice(s)</div>
                </div>
                <div className="card stat">
                    <div className="label">Outstanding</div>
                    <div className="value" style={{ color: "var(--warn)" }}>{money(data.income.income_outstanding)}</div>
                    <div className="sub">{data.income.invoices_outstanding} awaiting payment</div>
                </div>
                <div className="card stat">
                    <div className="label">Total expenditure</div>
                    <div className="value">{money(data.totalExpenditure)}</div>
                    <div className="sub">wages + super + expenses</div>
                </div>
                <div className="card stat">
                    <div className="label">Operating result</div>
                    <div className="value" style={{ color: data.profit >= 0 ? "var(--pass)" : "var(--block)" }}>
                        {money(data.profit)}
                    </div>
                    <div className="sub">cash basis, FY {fy - 1}–{String(fy).slice(2)}</div>
                </div>
            </div>

            <div className="card">
                <div className="card-head">
                    <h2>Margin on billed shifts</h2>
                    {data.margin.margin < 0 && <span className="chip block">negative margin</span>}
                </div>
                <div className="card-body">
                    <div className="grid cols-3">
                        <div><div className="doc-label">Billed</div><div style={{ fontSize: "1.3rem", fontWeight: 600 }}>{money(data.margin.billed)}</div></div>
                        <div><div className="doc-label">Pay cost incl. super</div><div style={{ fontSize: "1.3rem", fontWeight: 600 }}>{money(data.margin.cost)}</div></div>
                        <div><div className="doc-label">Margin</div>
                            <div style={{ fontSize: "1.3rem", fontWeight: 700, color: data.margin.margin >= 0 ? "var(--pass)" : "var(--block)" }}>
                                {money(data.margin.margin)}
                            </div>
                        </div>
                    </div>
                    <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginTop: "0.6rem" }}>
                        Shifts that appear on both an invoice and a timesheet in this FY. A negative
                        margin means a service is being delivered below cost — check the agreement rate.
                    </p>
                </div>
            </div>

            <div className="grid cols-2">
                <div className="card">
                    <div className="card-head">
                        <h2>Wages &amp; payroll</h2>
                        {/* Fetched with the auth header — a bare link would 401. */}
                        <button className="icon-btn" onClick={() => void downloadPayrollCsv(fy)}>
                            Download payroll CSV
                        </button>
                    </div>
                    <div className="card-body">
                        <dl className="kv">
                            <dt>Gross wages paid</dt><dd>{money(data.wages.wages_paid)}</dd>
                            <dt>Superannuation paid</dt><dd>{money(data.wages.super_paid)}</dd>
                            <dt>PAYG withheld</dt><dd>{money(data.wages.payg_withheld)}</dd>
                            <dt>Approved, not yet paid</dt><dd>{money(data.wages.wages_approved_unpaid)}</dd>
                        </dl>
                    </div>
                </div>
                <div className="card">
                    <div className="card-head"><h2>GST &amp; adjustments</h2></div>
                    <div className="card-body">
                        <dl className="kv">
                            <dt>GST collected (sales)</dt><dd>{money(data.income.gst_collected)}</dd>
                            <dt>GST paid (purchases)</dt><dd>{money(data.expenses.gst_paid)}</dd>
                            <dt>Discounts given</dt><dd>{money(data.income.discounts_given)}</dd>
                            <dt>Other expenses</dt><dd>{money(data.expenses.other_expenses)}</dd>
                        </dl>
                        <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginTop: "0.7rem" }}>
                            These figures summarise the documents in this system for your accountant.
                            BAS and the tax return are still prepared from your accounting records —
                            this is the working picture, not the lodgement.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ExpensesTab = () => {
    const expenses = useResource<ExpenseRow[]>("/expenses");
    const [search, setSearch] = useState("");
    const [dialog, setDialog] = useState<null | "create" | { edit: ExpenseRow } | { remove: ExpenseRow }>(null);

    const fields: Field[] = [
        { name: "expense_date", label: "Date", kind: "date", required: true, half: true },
        { name: "category", label: "Category", kind: "select", required: true, half: true, options: [
            "Insurance", "Vehicle & travel", "Equipment & PPE", "Software & subscriptions",
            "Rent & utilities", "Training", "Accounting & legal", "Marketing", "Other",
        ].map((c) => ({ value: c, label: c })) },
        { name: "description", label: "Description", kind: "text", required: true },
        { name: "supplier", label: "Supplier", kind: "text", half: true },
        { name: "amount", label: "Amount (inc GST)", kind: "number", required: true, min: 0, half: true },
        { name: "gst_amount", label: "GST portion", kind: "number", min: 0, half: true,
          help: "For a 10% GST-inclusive amount this is one-eleventh of the total." },
    ];

    const all = expenses.data ?? [];
    const filtered = all.filter((e) => matches(search, e.description, e.category, e.supplier));

    return (
        <div>
            {dialog === "create" && (
                <EntityForm title="Add Expense" path="/expenses" fields={fields}
                    onClose={() => setDialog(null)} onSaved={() => { expenses.reload(); setDialog(null); }} />
            )}
            {dialog && dialog !== "create" && "edit" in dialog && (
                <EntityForm title="Edit expense" path="/expenses" fields={fields} initial={dialog.edit}
                    onClose={() => setDialog(null)} onSaved={() => { expenses.reload(); setDialog(null); }} />
            )}
            {dialog && dialog !== "create" && "remove" in dialog && (
                <ConfirmDialog title="Delete this expense?" tone="danger"
                    message={`${dialog.remove.description} — ${money(dialog.remove.amount)}`}
                    warning="Expenses feed the FY summary; delete only genuine mistakes."
                    confirmLabel="Delete expense"
                    request={{ method: "delete", path: `/expenses/${dialog.remove.id}` }}
                    onClose={() => setDialog(null)} onDone={() => { expenses.reload(); setDialog(null); }} />
            )}

            <FilterBar search={search} onSearch={setSearch} searchPlaceholder="Search expenses…"
                action={<button className="btn" onClick={() => setDialog("create")}>+ Add Expense</button>} />

            <div className="card"><div className="card-body flush"><div className="table-wrap">
                <table>
                    <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Supplier</th>
                        <th>Amount</th><th>GST</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
                    <tbody>
                        {filtered.map((expense) => (
                            <tr key={expense.id}>
                                <td className="mono">{formatDate(expense.expense_date)}</td>
                                <td><span className="chip info">{expense.category}</span></td>
                                <td>{expense.description}</td>
                                <td>{expense.supplier ?? "—"}</td>
                                <td className="mono">{money(expense.amount)}</td>
                                <td className="mono">{money(expense.gst_amount)}</td>
                                <td><div className="row-actions">
                                    <button className="icon-btn" onClick={() => setDialog({ edit: expense })}>Edit</button>
                                    <button className="icon-btn danger" onClick={() => setDialog({ remove: expense })}>Delete</button>
                                </div></td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={7} className="empty">No expenses recorded for the filter.</td></tr>
                        )}
                    </tbody>
                </table>
            </div></div></div>
        </div>
    );
};

const RatesTab = () => {
    const rates = useResource<RateRow[]>("/rates");
    const companies = useResource<Company[]>("/companies");
    const roles = useResource<JobRole[]>("/roles");
    const serviceRates = useResource<Array<{ id: string; company_id: string; job_role_id: string; hourly_rate: string }>>("/company-service-rates");

    const [grid, setGrid] = useState<Record<string, number>>({});
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [addingAgreement, setAddingAgreement] = useState(false);

    useEffect(() => {
        if (!rates.data) return;
        const next: Record<string, number> = {};
        for (const row of rates.data) next[`${row.scope}.${row.category}`] = Number(row.multiplier);
        setGrid(next);
    }, [rates.data]);

    const save = async () => {
        setSaving(true);
        setMessage(null);
        try {
            await api.put("/rates", {
                rates: Object.entries(grid).map(([key, multiplier]) => {
                    const [scope, category] = key.split(".");
                    return { scope, category, multiplier };
                }),
            });
            setMessage("Saved. New documents use the new multipliers; issued documents keep theirs.");
        } catch (err) {
            setMessage(err instanceof Error ? err.message : "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const companyName = (id: string) => (companies.data ?? []).find((c) => c.id === id)?.name ?? "?";
    const roleName = (id: string) => (roles.data ?? []).find((r) => r.id === id)?.name ?? "?";

    return (
        <div style={{ display: "grid", gap: "1rem" }}>
            <div className="card">
                <div className="card-head">
                    <div>
                        <h2>Penalty-rate multipliers</h2>
                        <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginTop: 2 }}>
                            Applied to the base rate by when the shift starts (Perth time), highest category wins.
                            Defaults follow the SCHADS shape at permanent rates — review against your award and
                            agreements (casual loadings differ) before running real payrolls.
                        </p>
                    </div>
                    <button className="btn" onClick={() => void save()} disabled={saving}>
                        {saving ? "Saving…" : "Save multipliers"}
                    </button>
                </div>
                <div className="card-body flush"><div className="table-wrap">
                    <table>
                        <thead><tr><th>When the shift starts</th><th>Pay (permanent)</th><th>Pay (casual, loading embedded)</th><th>Bill (providers)</th></tr></thead>
                        <tbody>
                            {CATEGORIES.map(([key, label]) => (
                                <tr key={key}>
                                    <td style={{ fontWeight: 600 }}>{label}</td>
                                    {(["pay", "pay_casual", "bill"] as const).map((scope) => (
                                        <td key={scope}>
                                            <input type="number" step="0.005" min={1} max={5}
                                                style={{ width: 110 }}
                                                value={grid[`${scope}.${key}`] ?? 1}
                                                aria-label={`${scope} multiplier for ${label}`}
                                                onChange={(e) => setGrid((g) => ({
                                                    ...g, [`${scope}.${key}`]: Number(e.target.value),
                                                }))} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div></div>
                {message && <div className="card-body" style={{ borderTop: "1px solid var(--line-soft)", fontSize: "0.8rem", color: "var(--muted)" }}>{message}</div>}
            </div>

            <div className="card">
                <div className="card-head">
                    <div>
                        <h2>Provider service agreements</h2>
                        <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginTop: 2 }}>
                            The negotiated base rate per service for each organisation — your margin lives here.
                            Where no agreement rate exists, the role's default bill rate applies.
                        </p>
                    </div>
                    <button className="btn" onClick={() => setAddingAgreement(true)}>+ Agreement rate</button>
                </div>
                <div className="card-body flush"><div className="table-wrap">
                    <table>
                        <thead><tr><th>Organisation</th><th>Service</th><th>Agreed base rate /hr</th></tr></thead>
                        <tbody>
                            {(serviceRates.data ?? []).map((rate) => (
                                <tr key={rate.id}>
                                    <td style={{ fontWeight: 600 }}>{companyName(rate.company_id)}</td>
                                    <td>{roleName(rate.job_role_id)}</td>
                                    <td className="mono">{money(rate.hourly_rate)}</td>
                                </tr>
                            ))}
                            {(serviceRates.data ?? []).length === 0 && (
                                <tr><td colSpan={3} className="empty">No agreement rates yet — role defaults apply.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div></div>
            </div>

            {addingAgreement && (
                <EntityForm title="Agreement rate" path="/company-service-rates"
                    fields={[
                        { name: "company_id", label: "Organisation", kind: "select", required: true,
                          options: (companies.data ?? []).map((c) => ({ value: c.id, label: c.name })) },
                        { name: "job_role_id", label: "Service (role)", kind: "select", required: true,
                          options: (roles.data ?? []).map((r) => ({ value: r.id, label: r.name })) },
                        { name: "hourly_rate", label: "Agreed base rate per hour", kind: "number", required: true, min: 0 },
                        { name: "notes", label: "Notes (quote reference etc.)", kind: "textarea" },
                    ]}
                    onClose={() => setAddingAgreement(false)}
                    onSaved={() => { serviceRates.reload(); setAddingAgreement(false); }} />
            )}
        </div>
    );
};

const StatementsTab = ({ fy }: { fy: number }) => {
    const employees = useResource<Employee[]>("/employees");
    const companies = useResource<Company[]>("/companies");
    const [showing, setShowing] = useState<null | { kind: "staff"; id: string } | { kind: "provider"; id: string }>(null);

    return (
        <div className="grid cols-2">
            <div className="card">
                <div className="card-head"><h2>Staff year-end statements</h2></div>
                <div className="card-body flush"><div className="table-wrap"><table><tbody>
                    {(employees.data ?? []).map((employee) => (
                        <tr key={employee.id}>
                            <td>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                    <InitialsAvatar name={employee.full_name} size={24} />
                                    {employee.full_name}
                                </div>
                            </td>
                            <td style={{ textAlign: "right" }}>
                                <button className="icon-btn" onClick={() => setShowing({ kind: "staff", id: employee.id })}>
                                    FY {fy - 1}–{String(fy).slice(2)} statement
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody></table></div></div>
            </div>
            <div className="card">
                <div className="card-head"><h2>Provider year-end statements</h2></div>
                <div className="card-body flush"><div className="table-wrap"><table><tbody>
                    {(companies.data ?? []).map((company) => (
                        <tr key={company.id}>
                            <td style={{ fontWeight: 600 }}>{company.name}</td>
                            <td style={{ textAlign: "right" }}>
                                <button className="icon-btn" onClick={() => setShowing({ kind: "provider", id: company.id })}>
                                    FY {fy - 1}–{String(fy).slice(2)} statement
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody></table></div></div>
            </div>

            {showing?.kind === "staff" && (
                <StaffStatement employeeId={showing.id} fy={fy} onClose={() => setShowing(null)} />
            )}
            {showing?.kind === "provider" && (
                <ProviderStatement companyId={showing.id} fy={fy} onClose={() => setShowing(null)} />
            )}
        </div>
    );
};

const StaffStatement = ({ employeeId, fy, onClose }: { employeeId: string; fy: number; onClose: () => void }) => {
    const data = useResource<{
        employee: { full_name: string; employment_type: string; abn: string | null };
        timesheets: Array<{ timesheet_number: string; period_start: string; period_end: string;
            total_hours: string; total_amount: string; tax_withheld: string; super_amount: string; net_pay: string }>;
        totals: { hours: number; gross: number; tax: number; super: number; net: number };
    }>(`/finance/staff-statement/${employeeId}?fy=${fy}`);
    const d = data.data;

    return (
        <PrintDoc kind="ANNUAL PAYMENT STATEMENT" number={`FY ${fy - 1}–${String(fy).slice(2)}`} onClose={onClose}>
            {!d ? <p>Loading…</p> : (
                <>
                    <div className="doc-meta">
                        <div>
                            <div className="doc-label">Worker</div>
                            <div style={{ fontWeight: 700 }}>{d.employee.full_name}</div>
                            <div>{titleCase(d.employee.employment_type)}{d.employee.abn ? ` · ABN ${d.employee.abn}` : " · TFN employee"}</div>
                        </div>
                        <div>
                            <div className="doc-label">Period</div>
                            <div>1 July {fy - 1} – 30 June {fy}</div>
                        </div>
                    </div>
                    <table className="doc-table">
                        <thead><tr><th>Timesheet</th><th>Period</th><th className="num">Hours</th>
                            <th className="num">Gross</th><th className="num">PAYG*</th>
                            <th className="num">Super</th><th className="num">Net</th></tr></thead>
                        <tbody>
                            {d.timesheets.map((t) => (
                                <tr key={t.timesheet_number}>
                                    <td>{t.timesheet_number}</td>
                                    <td>{formatDate(t.period_start)} – {formatDate(t.period_end)}</td>
                                    <td className="num">{Number(t.total_hours).toFixed(2)}</td>
                                    <td className="num">{money(t.total_amount)}</td>
                                    <td className="num">{money(t.tax_withheld)}</td>
                                    <td className="num">{money(t.super_amount)}</td>
                                    <td className="num">{money(t.net_pay)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="doc-total">
                                <td colSpan={2} className="num">Totals</td>
                                <td className="num">{d.totals.hours.toFixed(2)}</td>
                                <td className="num">{money(d.totals.gross)}</td>
                                <td className="num">{money(d.totals.tax)}</td>
                                <td className="num">{money(d.totals.super)}</td>
                                <td className="num">{money(d.totals.net)}</td>
                            </tr>
                        </tfoot>
                    </table>
                    <p className="doc-notes">
                        *PAYG figures are the amounts withheld under the flat estimate agreed with the worker.
                        The authoritative income statement is issued through ATO Single Touch Payroll by your
                        payroll software; this statement summarises the payments recorded in this system.
                    </p>
                </>
            )}
        </PrintDoc>
    );
};

const ProviderStatement = ({ companyId, fy, onClose }: { companyId: string; fy: number; onClose: () => void }) => {
    const data = useResource<{
        company: { name: string; abn: string | null; email: string | null };
        invoices: Array<{ invoice_number: string; issue_date: string; status: string;
            subtotal: string; discount_amount: string; gst_amount: string; total: string }>;
        totals: { billed: number; gst: number; discounts: number; paid: number; outstanding: number };
    }>(`/finance/provider-statement/${companyId}?fy=${fy}`);
    const d = data.data;

    return (
        <PrintDoc kind="ANNUAL STATEMENT" number={`FY ${fy - 1}–${String(fy).slice(2)}`} onClose={onClose}
            emailTo={d?.company.email ?? null}
            emailSubject={`Annual statement FY ${fy - 1}-${String(fy).slice(2)} — ${d?.company.name ?? ""}`}>
            {!d ? <p>Loading…</p> : (
                <>
                    <div className="doc-meta">
                        <div>
                            <div className="doc-label">Statement for</div>
                            <div style={{ fontWeight: 700 }}>{d.company.name}</div>
                            {d.company.abn && <div>ABN {d.company.abn}</div>}
                        </div>
                        <div>
                            <div className="doc-label">Period</div>
                            <div>1 July {fy - 1} – 30 June {fy}</div>
                        </div>
                    </div>
                    <table className="doc-table">
                        <thead><tr><th>Invoice</th><th>Issued</th><th>Status</th>
                            <th className="num">Subtotal</th><th className="num">Discount</th>
                            <th className="num">GST</th><th className="num">Total</th></tr></thead>
                        <tbody>
                            {d.invoices.map((invoice) => (
                                <tr key={invoice.invoice_number}>
                                    <td>{invoice.invoice_number}</td>
                                    <td>{formatDate(invoice.issue_date)}</td>
                                    <td>{titleCase(invoice.status)}</td>
                                    <td className="num">{money(invoice.subtotal)}</td>
                                    <td className="num">{money(invoice.discount_amount)}</td>
                                    <td className="num">{money(invoice.gst_amount)}</td>
                                    <td className="num">{money(invoice.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr><td colSpan={6} className="num">Total paid</td><td className="num">{money(d.totals.paid)}</td></tr>
                            <tr><td colSpan={6} className="num">Outstanding</td><td className="num">{money(d.totals.outstanding)}</td></tr>
                            <tr className="doc-total"><td colSpan={6} className="num">Total billed</td><td className="num">{money(d.totals.billed)}</td></tr>
                        </tfoot>
                    </table>
                </>
            )}
        </PrintDoc>
    );
};
